import { get } from 'fe-common-xhr'

import { microFrontendManifestService, remoteRepository } from './constants'
import {
  getMicroFrontendMetadata,
  mergeMicroFrontendMetadata,
  type MicroFrontendManifest,
  type MicroFrontendServerConfiguration
} from './manifest'
import logger from '../log'

export type MicroFrontendQueryParams = Record<string, string>
export type { MicroFrontendManifest, MicroFrontendServerConfiguration } from './manifest'

/*
 * Fetch configuration from server
 */
const fetchServerConfiguration = async (): Promise<MicroFrontendServerConfiguration> => {
  try {
    const response = await get<MicroFrontendServerConfiguration>(microFrontendManifestService)
    return response.data
  } catch (error) {
    logger.error(`Failed to fetch micro frontend configuration from ${microFrontendManifestService}`, error)
    throw new Error(`Failed to load micro frontend configuration`)
  }
}

/*
 * Get versions from query params
 * url?fe-workshop-history=1.3.4&fe-campaign=3.2.1
 */
export const getQueryParamVersions = (search: string): MicroFrontendQueryParams =>
  Array.from(new URLSearchParams(search).entries()).reduce<MicroFrontendQueryParams>(
    (result, [mf, version]) => ({
      ...result,
      [mf]: version
    }),
    {}
  )

const resolveQueryParamManifest = async (
  microFrontend: string,
  defaultManifest: MicroFrontendManifest,
  queryParamVersion?: string
): Promise<[string, MicroFrontendManifest]> => {
  if (!queryParamVersion) {
    return [microFrontend, defaultManifest]
  }

  const versionManifest = { ...defaultManifest, version: queryParamVersion }
  if (queryParamVersion === 'null') {
    return [microFrontend, versionManifest]
  }

  const metadata = await getMicroFrontendMetadata(`${remoteRepository}/${microFrontend}/${queryParamVersion}`)
  return [microFrontend, mergeMicroFrontendMetadata(versionManifest, metadata)]
}

/*
 * Add versions from query params to config and override metadata for query param versions when possible
 */
export const addQueryParamConfig = async (
  configuration: MicroFrontendServerConfiguration,
  queryParams: MicroFrontendQueryParams
): Promise<MicroFrontendServerConfiguration> =>
  Object.fromEntries(
    await Promise.all(
      Object.entries(configuration).map(
        async ([mf, defaultManifest]) => await resolveQueryParamManifest(mf, defaultManifest, queryParams[mf])
      )
    )
  )

/*
 * Get the micro frontend configuration
 */
export const getConfiguration = async (): Promise<MicroFrontendServerConfiguration> => {
  const serverConfig = await fetchServerConfiguration()
  const queryParams = getQueryParamVersions(window.location.search)
  return await addQueryParamConfig(serverConfig, queryParams)
}
